import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  ConnectRemoteMcpDto,
  RemoteMcpConnectResult,
  RemoteMcpCreateDto,
  RemoteMcpDto,
  RemoteMcpOAuthResultMessage,
  RemoteMcpOAuthStartDto,
  RemoteMcpOAuthStartResult,
  RemoteMcpUpdateDto,
} from '@/Domain/api/Action/dto/RemoteMCP.dto';
import {
  PostHogEventEnum,
  type RemoteMcpOAuthFailReason,
  type RemoteMcpOAuthSettledVia,
} from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useModalStore } from '@/Domain/store/modal.store';

const POPUP_FEATURES =
  'menubar=no,location=no,resizable=no,scrollbars=no,status=no,height=800,width=600';
// The consent popup navigates to the third-party provider, whose Cross-Origin-Opener-Policy
// severs `window.opener` and makes `popup.closed` unreliable. So we never trust the popup
// handle: completion is detected by polling the action's own OAuth status on our backend
// (plus a localStorage fast-path the callback writes on our origin, and postMessage when the
// provider does not set COOP).
const STATUS_POLL_MS = 1500;
const OAUTH_TIMEOUT_MS = 180_000;
const RESULT_STORAGE_KEY = 'remote_mcp_oauth_result';
// A duplicate callback (most often a browser prefetch of the redirect URL) races the real one on
// the single-use state: one request wins and authorizes, the other gets "state already claimed"
// and returns an error. These reason codes are therefore ambiguous, not terminal, so ignore them
// and let the status poll settle success (or the timeout settle failure) from the backend's truth.
const AMBIGUOUS_OAUTH_RESULT_REASONS = new Set(['invalid_state', 'state_unavailable']);

export type OAuthPhase = 'idle' | 'pre_consent' | 'authorizing' | 'finalizing';

type Params = {
  remoteMcpAction?: RemoteMcpDto;
  getConnectFormData: () => ConnectRemoteMcpDto;
};

const fetchActionStatus = (actionId: number) =>
  queryClient.fetchQuery<RemoteMcpDto>({
    queryKey: [QueryKey.actions.remoteMcp.get, actionId],
    staleTime: 0,
  });

export const useRemoteMcpOAuth = ({ remoteMcpAction, getConnectFormData }: Params) => {
  const { t } = useTranslation('remote_mcp');
  const { setParams } = useModalStore();
  const { capture } = usePostHogAnalytics();

  const [phase, setPhase] = useState<OAuthPhase>('idle');
  const [providerLabel, setProviderLabel] = useState<string>(
    remoteMcpAction?.oauth?.provider_label ?? '',
  );
  // Async flow callbacks are intentionally long-lived (popup polling can run for minutes). Keep
  // the current label in a ref as well as state so telemetry and toasts do not capture the blank
  // label from the render before oauth/start returns.
  const providerLabelRef = useRef(providerLabel);
  const updateProviderLabel = useCallback((label: string) => {
    providerLabelRef.current = label;
    setProviderLabel(label);
  }, []);
  const [allowRegularProbe, setAllowRegularProbe] = useState(false);

  const popupRef = useRef<Window | null>(null);
  // Holds ONLY a throwaway draft THIS flow created for a brand-new action; an existing action is
  // never placed here, so the abandoned-draft reaper can't delete a real action once closeModal
  // nulls remoteMcpAction on close (which would otherwise defeat the guard in cleanupDraftRef).
  const draftRef = useRef<RemoteMcpDto | undefined>(undefined);
  const actionIdRef = useRef<number | undefined>(remoteMcpAction?.id);
  const oauthStateRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  const statusSettlementRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupDraftRef = useRef<() => void>(() => {});
  const cleanupUnmountedFlowRef = useRef<() => void>(() => {});
  // A failed/timed-out flow on an EXISTING action must revert to its status view ('idle'), not the
  // 'pre_consent' panel that would misrepresent a still-connected server as disconnected.
  const revertPhaseRef = useRef<OAuthPhase>('pre_consent');
  const openingRef = useRef(false);
  // Consent-funnel telemetry: when the popup opened, and which of the three signals settled it.
  // Drift toward 'status_poll' means the postMessage/localStorage paths are being severed.
  const consentStartedAtRef = useRef<number | null>(null);
  const isOAuthAttemptPendingRef = useRef(false);
  const isCancelRequestedRef = useRef(false);
  const settledViaRef = useRef<RemoteMcpOAuthSettledVia>('status_poll');

  const captureOAuthFailure = useCallback(
    (reason: RemoteMcpOAuthFailReason) => {
      const consentStartedAt = consentStartedAtRef.current;
      isOAuthAttemptPendingRef.current = false;
      isCancelRequestedRef.current = false;
      capture(PostHogEventEnum.RemoteMcpOauthFailed, {
        provider_label: providerLabelRef.current,
        reason,
        elapsed_ms: consentStartedAt === null ? null : Math.max(0, Date.now() - consentStartedAt),
      });
    },
    [capture],
  );

  const createRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpCreateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.create],
    // Draft created only to carry the OAuth flow; suppress the shared "created" toast
    // and list-append until authorization finalizes.
    onSuccess: () => {},
  });
  // Persist form edits before authorizing; suppress the shared "updated" toast (the
  // OAuth flow owns user feedback here).
  const updateRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpUpdateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.update],
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKey.actions.remoteMcp.get, data.id], data);
      queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
        old?.map((action) => (action.id === data.id ? data : action)),
      );
    },
  });
  const oauthStartMutation = useMutation<
    RemoteMcpOAuthStartResult,
    unknown,
    RemoteMcpOAuthStartDto
  >({ mutationKey: [QueryKey.actions.remoteMcp.oauthStart] });
  const oauthDisconnectMutation = useMutation<RemoteMcpDto, unknown, { id: number }>({
    mutationKey: [QueryKey.actions.remoteMcp.oauthDisconnect],
  });
  const deleteDraftMutation = useMutation<number, unknown, number>({
    mutationKey: [QueryKey.actions.basic.delete],
    onSuccess: () => {},
  });
  // Auto-enable via the shared basic-action PATCH (the remote-mcp PATCH endpoint doesn't apply is_active) so its list cache stays in sync.
  const activateActionMutation = useMutation<
    RemoteMcpDto,
    unknown,
    { id: number; is_active: boolean }
  >({
    mutationKey: [QueryKey.actions.basic.patch],
    onSuccess: () => {},
  });

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Delete the throwaway draft, but only after confirming it never became authorized, so a
  // connection that succeeded server-side (even if the popup signalling was lost) is never
  // destroyed. A brand-new action's draft lives in draftRef; existing actions are left alone.
  cleanupDraftRef.current = () => {
    const draft = draftRef.current;
    if (!draft || remoteMcpAction) return;
    draftRef.current = undefined;
    void (async () => {
      try {
        const action = await fetchActionStatus(draft.id);
        if (action?.oauth?.status === 'authorized' || action?.oauth?.status === 'finalizing')
          return;
      } catch {
        return; // can't verify authorization state -> keep the draft (deleting could drop a live connection); it remains an inactive action the user can remove
      }
      deleteDraftMutation.mutate(draft.id);
    })();
  };

  // The unmount effect intentionally has stable dependencies, so keep the current mutation and
  // action/state refs behind a callable ref. Once a state was issued, closing the sheet is a real
  // cancellation: rotate the generation before reaping a draft so a redirect already in flight
  // cannot authorize an existing action after its UI has gone away.
  cleanupUnmountedFlowRef.current = () => {
    const actionId = actionIdRef.current;
    if (isOAuthAttemptPendingRef.current)
      captureOAuthFailure(isCancelRequestedRef.current ? 'cancelled' : 'sheet_closed');
    if (!oauthStateRef.current || actionId === undefined) {
      cleanupDraftRef.current();
      return;
    }
    void oauthDisconnectMutation
      .mutateAsync({ id: actionId })
      .catch(() => undefined)
      .finally(() => cleanupDraftRef.current());
  };

  const beginConsent = useCallback(
    (result: RemoteMcpConnectResult) => {
      updateProviderLabel(result.provider_label || '');
      setAllowRegularProbe(false);
      setPhase('pre_consent');
    },
    [updateProviderLabel],
  );

  const finalize = useCallback(
    async (actionId: number) => {
      setPhase('finalizing');
      try {
        let action = await fetchActionStatus(actionId);
        // A success signal can race an older status request already in React Query's in-flight
        // cache. Once that request resolves, issue one fresh read before declaring the committed
        // callback incomplete; postMessage/localStorage are emitted only after the backend commit.
        if (action?.oauth?.status !== 'authorized') action = await fetchActionStatus(actionId);
        // Only a genuinely authorized action may report success. Settling on a signal the backend
        // has not caught up with (or a lost race with a cancel) would otherwise toast "Connected"
        // and emit oauth_completed over a panel still reading "Authorization required".
        if (action?.oauth?.status !== 'authorized') {
          throw new Error('not_authorized');
        }
        // Auto-enable a freshly connected action so people don't connect, save, and then wonder why
        // nothing happens because the toggle is still off (regular AI actions enable the same way).
        if (!action.is_active) {
          try {
            await activateActionMutation.mutateAsync({ id: actionId, is_active: true });
            action = { ...action, is_active: true };
          } catch {
            /* leave it off (e.g. the action limit is reached); the user can enable it manually */
          }
        }
        // Confirmed authorized, so this is a real action: only now is it safe to stop the
        // abandoned-draft reaper from considering it.
        draftRef.current = undefined;
        setParams({ remoteMcpAction: action });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
        const finalizedProvider = action.oauth?.provider_label || providerLabelRef.current;
        capture(PostHogEventEnum.RemoteMcpOauthCompleted, {
          provider_label: finalizedProvider,
          tool_count: action?.allowed_tools?.length ?? 0,
          duration_ms:
            consentStartedAtRef.current !== null
              ? Math.max(0, Date.now() - consentStartedAtRef.current)
              : null,
          settled_via: settledViaRef.current,
        });
        toast.success(t('oauth.connected', { provider: finalizedProvider }));
        setPhase('idle');
      } catch {
        // A transient post-auth fetch failure must not misreport an existing, authorized action as
        // disconnected: revert to its status view ('idle'), not the "Authorization required" panel.
        captureOAuthFailure('finalize_failed');
        toast.error(t('oauth.finalize_failed'));
        setPhase(revertPhaseRef.current);
      } finally {
        popupRef.current?.close();
        popupRef.current = null;
        clearPoll();
      }
    },
    [activateActionMutation, capture, captureOAuthFailure, clearPoll, setParams, t],
  );

  const settleSuccess = useCallback(
    (actionId: number, settledVia: RemoteMcpOAuthSettledVia) => {
      if (settledRef.current) return;
      settledRef.current = true;
      isOAuthAttemptPendingRef.current = false;
      isCancelRequestedRef.current = false;
      // The callback has committed this flow; a later sheet unmount must not interpret the old
      // state as still pending and revoke the newly authorized action.
      oauthStateRef.current = null;
      settledViaRef.current = settledVia;
      clearPoll();
      void finalize(actionId);
    },
    [clearPoll, finalize],
  );

  const settleFailure = useCallback(
    (revertPhase: OAuthPhase, reason: RemoteMcpOAuthFailReason, toastKey?: string) => {
      if (settledRef.current) return;
      settledRef.current = true;
      // Error callbacks consume state server-side, so there is no late success left to invalidate.
      oauthStateRef.current = null;
      clearPoll();
      popupRef.current?.close();
      popupRef.current = null;
      cleanupDraftRef.current();
      captureOAuthFailure(reason);
      setPhase(revertPhase);
      // Reflect the backend state, as settleByStatus does: oauth/start already revoked the prior
      // grant and deactivated the action, so a denied reconnect would otherwise keep rendering the
      // stale "Connected" panel for an action that is offline and holds no token.
      const settledActionId = actionIdRef.current;
      if (remoteMcpAction && settledActionId) {
        void fetchActionStatus(settledActionId)
          .then((action) => setParams({ remoteMcpAction: action }))
          .catch(() => {});
      }
      if (toastKey) toast.error(t(toastKey));
    },
    [captureOAuthFailure, clearPoll, remoteMcpAction, setParams, t],
  );

  // For an explicit cancel or a timeout: the popup told us nothing, so ask the backend. If the
  // user actually finished, recover into the connected state; otherwise drop the draft.
  const settleByStatus = useCallback(
    async (actionId: number | undefined, revertPhase: OAuthPhase, toastKey?: string) => {
      if (settledRef.current) return;
      // An existing action may still report its *previous* grant as authorized while the new
      // oauth/start request is in flight. Treating that status as this flow's success would let a
      // cancel toast "Connected" just before start deactivates it. Claim the cancel now; the
      // request owner will either stop before start or locally revoke after a successful start.
      if (openingRef.current && !oauthStateRef.current) {
        settledRef.current = true;
        clearPoll();
        popupRef.current?.close();
        popupRef.current = null;
        captureOAuthFailure('cancelled');
        setPhase(revertPhase);
        return;
      }
      if (actionId === undefined) {
        // actionId is still undefined while the draft create + oauth/start are in flight, and the
        // Cancel button is already on screen. Claim the flow so the in-flight openConsentPopup
        // aborts instead of navigating the popup and settling behind the user's back.
        settledRef.current = true;
        clearPoll();
        popupRef.current?.close();
        popupRef.current = null;
        captureOAuthFailure('cancelled');
        setPhase(revertPhase);
        return;
      }
      // The interval can cross the timeout boundary more than once while this fetch/revoke is in
      // flight. Only one status-based settlement may own the flow.
      if (statusSettlementRef.current) return;
      statusSettlementRef.current = true;
      clearPoll();
      let authorized = false;
      let action: RemoteMcpDto | undefined;
      try {
        action = await fetchActionStatus(actionId);
        authorized = action?.oauth?.status === 'authorized';
      } catch {
        authorized = false;
      }
      // A callback/localStorage signal can settle while the read above is in flight. In
      // particular, its stale response may still say FINALIZING even though the callback has now
      // committed; never revoke after another settlement path has already claimed success/error.
      if (settledRef.current) return;
      if (authorized) {
        settleSuccess(actionId, 'status_poll');
        return;
      }
      // Once oauth/start has issued a state, an explicit cancel/timeout must rotate the credential
      // generation even if the callback has not reached FINALIZING yet. Otherwise a redirect that
      // was already in flight could arrive after the user cancelled and authorize the existing
      // action behind their back. FINALIZING recovery also covers a flow resumed after remount,
      // where this tab no longer knows the original state. The revoke endpoint's generation CAS
      // makes both cases safe: whichever operation commits last wins without resurrecting a
      // cancelled token.
      if (oauthStateRef.current || action?.oauth?.status === 'finalizing') {
        try {
          action = await oauthDisconnectMutation.mutateAsync({ id: actionId });
          if (remoteMcpAction) setParams({ remoteMcpAction: action });
        } catch {
          // Keep the flow unsettled so the still-visible Cancel button can retry recovery.
          statusSettlementRef.current = false;
          return;
        }
      }
      // Re-check: a postMessage can settle the flow while the status read above is in flight, and
      // that read can still report needs_authorization (commit lag). Without this the flow settles
      // twice, double-counting the funnel in both directions and overwriting finalize's phase.
      if (settledRef.current) return;
      settledRef.current = true;
      oauthStateRef.current = null;
      popupRef.current?.close();
      popupRef.current = null;
      cleanupDraftRef.current();
      captureOAuthFailure(toastKey === 'oauth.timed_out' ? 'timed_out' : 'cancelled');
      // Reflect the just-fetched backend state so an abandoned reconnect stops showing a stale
      // "Connected" panel: the action is now pending re-authorization (and offline) until retried.
      if (remoteMcpAction && action) setParams({ remoteMcpAction: action });
      setPhase(revertPhase);
      if (toastKey) toast.info(t(toastKey));
    },
    [
      captureOAuthFailure,
      clearPoll,
      oauthDisconnectMutation,
      settleSuccess,
      remoteMcpAction,
      setParams,
      t,
    ],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The callback page is served from our own origin, so trust only same-origin. COOP can
      // null event.source, so we key on action_id + the settled guard instead of the handle.
      if (event.origin !== window.location.origin) return;
      const data = event.data as RemoteMcpOAuthResultMessage;
      if (!data || data.type !== 'remote_mcp_oauth_result') return;
      if (!oauthStateRef.current || data.state !== oauthStateRef.current) return;
      if (data.status === 'success') {
        if (data.action_id === null || data.action_id !== actionIdRef.current) return;
        settleSuccess(data.action_id, 'post_message');
        return;
      }
      // An expired/replayed state cannot be mapped back to an action server-side. Matching the
      // echoed OAuth state ties a null-id error to this tab's flow instead of another tab's popup.
      if (data.action_id !== null && data.action_id !== actionIdRef.current) return;
      // A duplicate/prefetched callback that lost the single-use-state race must not fail the flow
      // out from under the real one still finalizing — the status poll reconciles the true outcome.
      if (data.reason_code && AMBIGUOUS_OAUTH_RESULT_REASONS.has(data.reason_code)) return;
      settleFailure(revertPhaseRef.current, 'callback_error', 'oauth.error');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [settleSuccess, settleFailure]);

  // Close a lingering popup and invalidate/clean up only on unmount (deps are stable), so
  // re-subscribing effects never tear down an in-flight OAuth flow.
  useEffect(
    () => () => {
      // Claim the flow so an openConsentPopup still awaiting its create/start bails out instead of
      // navigating an orphan popup and installing an interval nothing can clear afterwards.
      settledRef.current = true;
      clearPoll();
      popupRef.current?.close();
      popupRef.current = null;
      cleanupUnmountedFlowRef.current();
    },
    [clearPoll],
  );

  const startWatcher = useCallback(
    (actionId: number) => {
      clearPoll();
      const startedAt = Date.now();
      pollRef.current = setInterval(() => {
        if (settledRef.current) return;
        // 1) Fast, COOP-proof path: the callback (our origin) wrote the outcome to localStorage.
        try {
          const raw = window.localStorage.getItem(RESULT_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as RemoteMcpOAuthResultMessage;
            const belongsToFlow =
              parsed?.state === oauthStateRef.current &&
              (parsed?.action_id === actionId ||
                (parsed?.status === 'error' && parsed?.action_id === null));
            if (belongsToFlow) {
              window.localStorage.removeItem(RESULT_STORAGE_KEY);
              if (parsed.status === 'success') {
                settleSuccess(actionId, 'local_storage');
                return;
              }
              // A duplicate/prefetched callback that lost the single-use-state race is ambiguous:
              // drop it and fall through to the backend status check, which the concurrent winner
              // will flip to authorized. Any other error is a real, terminal failure.
              if (!parsed.reason_code || !AMBIGUOUS_OAUTH_RESULT_REASONS.has(parsed.reason_code)) {
                settleFailure(revertPhaseRef.current, 'callback_error', 'oauth.error');
                return;
              }
            }
          }
        } catch {
          /* ignore malformed storage */
        }
        // 2) Reliable path: the backend exposes `finalizing` while it lists tools and flips to
        //    `authorized` only after that work completes. Status alone is therefore sufficient,
        //    including for a legitimate server that currently exposes zero tools.
        //    We deliberately do NOT watch popup.closed: a provider whose consent page sets COOP
        //    (e.g. Notion) neuters the handle so popup.closed reads true mid-consent, which would
        //    abort and delete a live, about-to-succeed connection. Genuine abandons fall back to (3).
        void fetchActionStatus(actionId)
          .then((action) => {
            if (action?.oauth?.status === 'authorized') {
              settleSuccess(actionId, 'status_poll');
            }
          })
          .catch(() => {});
        // 3) Give up after the timeout (re-checking status once more before reverting).
        if (Date.now() - startedAt > OAUTH_TIMEOUT_MS) {
          void settleByStatus(actionId, revertPhaseRef.current, 'oauth.timed_out');
        }
      }, STATUS_POLL_MS);
    },
    [clearPoll, settleSuccess, settleFailure, settleByStatus],
  );

  useEffect(() => {
    if (remoteMcpAction?.oauth?.status !== 'finalizing' || phase !== 'idle') return;
    settledRef.current = false;
    statusSettlementRef.current = false;
    oauthStateRef.current = null;
    actionIdRef.current = remoteMcpAction.id;
    revertPhaseRef.current = 'idle';
    startWatcher(remoteMcpAction.id);
  }, [phase, remoteMcpAction, startWatcher]);

  const openConsentPopup = useCallback(async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    consentStartedAtRef.current = null;
    isOAuthAttemptPendingRef.current = true;
    isCancelRequestedRef.current = false;
    capture(PostHogEventEnum.RemoteMcpOauthIntent, {
      provider_label: providerLabelRef.current,
      is_reconnect: !!remoteMcpAction,
    });
    setAllowRegularProbe(false);
    // window.open must run synchronously (pre-await) or the browser blocks it.
    settledRef.current = false;
    statusSettlementRef.current = false;
    oauthStateRef.current = null;
    // Clear any result a prior successful flow left behind (postMessage settled it before the poll
    // could consume it), so this flow's watcher can't false-finalize on a stale action_id match.
    try {
      window.localStorage.removeItem(RESULT_STORAGE_KEY);
    } catch {
      /* ignore unavailable storage */
    }
    revertPhaseRef.current = remoteMcpAction ? 'idle' : 'pre_consent';
    const popup = window.open('', '_blank', POPUP_FEATURES);
    if (!popup) {
      captureOAuthFailure('popup_blocked');
      toast.error(t('oauth.popup_blocked'));
      openingRef.current = false;
      return;
    }
    // Track the popup immediately: the create + start below are awaited, and a cancel or a sheet
    // close during that window must be able to close it.
    popupRef.current = popup;
    setPhase('authorizing');
    // Which leg failed, so the funnel separates "we could not persist the action" (usually the
    // plan's action limit, which is what the user is toasted about) from "the OAuth handshake
    // itself failed".
    let failureReason: RemoteMcpOAuthFailReason = 'start_failed';
    try {
      let action = remoteMcpAction ?? draftRef.current;
      if (!action) {
        failureReason = 'create_failed';
        action = await createRemoteMcpMutation.mutateAsync({
          ...getConnectFormData(),
          allowed_tools: [],
        });
        draftRef.current = action;
      } else {
        failureReason = 'create_failed';
        // Reusing an existing action/draft: persist ONLY the connection details (URL + headers)
        // shown in the form so OAuth authorizes the server on screen. Never send the catalog
        // name/icon/description here — the connect form doesn't hold them (they live on the sheet
        // form), so sending them would blank an active action's name, which fails validation and
        // flips the UI to a false "disconnected" state.
        const { remote_mcp_url, headers } = getConnectFormData();
        action = await updateRemoteMcpMutation.mutateAsync({
          id: action.id,
          remote_mcp_url,
          headers,
        });
        if (draftRef.current) draftRef.current = action;
      }
      actionIdRef.current = action.id;
      // A cancel or sheet close may have happened while the action create/update was in flight.
      // Stop before oauth/start so an existing action keeps its current grant.
      if (settledRef.current) {
        popup.close();
        // The PATCH may have changed the URL and therefore atomically removed the old grant even
        // though the user cancelled before oauth/start. Reflect that committed response instead
        // of leaving a stale Connected panel for credentials the backend has already discarded.
        if (remoteMcpAction) {
          setParams({ remoteMcpAction: action });
          updateProviderLabel(action.oauth?.provider_label ?? '');
          setAllowRegularProbe(!action.oauth);
        }
        cleanupDraftRef.current();
        return;
      }
      failureReason = 'start_failed';
      const { authorization_url, provider_label } = await oauthStartMutation.mutateAsync({
        action_id: action.id,
      });
      // The user can cancel or close the sheet while the two calls above are in flight. Both mark
      // the flow settled, so bail rather than navigating the popup and finalizing behind them.
      if (settledRef.current) {
        popup.close();
        // oauth/start already rotated the credential generation and took the action offline. Reset
        // that local grant before returning so cancelling during the request cannot leave a stale
        // "Connected" panel or a permanently pending draft.
        try {
          const resetAction = await oauthDisconnectMutation.mutateAsync({ id: action.id });
          if (remoteMcpAction) setParams({ remoteMcpAction: resetAction });
        } finally {
          cleanupDraftRef.current();
        }
        return;
      }
      const resolvedProviderLabel = provider_label || providerLabelRef.current;
      updateProviderLabel(resolvedProviderLabel);
      const oauthState = new URL(authorization_url).searchParams.get('state');
      if (!oauthState) throw new Error('missing_oauth_state');
      oauthStateRef.current = oauthState;
      // Sever the opener before handing the window to a server-controlled page, so a hostile
      // provider cannot navigate the authenticated dashboard tab. Completion never relies on the
      // opener link: it arrives via same-origin postMessage, localStorage, or the status poll.
      try {
        popup.opener = null;
      } catch {
        /* cross-origin popups may refuse the write; the flow does not depend on it */
      }
      popup.location.href = authorization_url;
      popupRef.current = popup;
      consentStartedAtRef.current = Date.now();
      settledViaRef.current = 'status_poll';
      capture(PostHogEventEnum.RemoteMcpOauthStarted, {
        provider_label: resolvedProviderLabel,
        is_reconnect: !!remoteMcpAction,
      });
      startWatcher(action.id);
    } catch {
      if (settledRef.current) {
        popup.close();
        popupRef.current = null;
        cleanupDraftRef.current();
        if (remoteMcpAction) {
          try {
            const refreshedAction = await fetchActionStatus(remoteMcpAction.id);
            setParams({ remoteMcpAction: refreshedAction });
          } catch {
            /* the cancel already owns user feedback; keep the last known action */
          }
        }
        setPhase(revertPhaseRef.current);
        return;
      }
      captureOAuthFailure(failureReason);
      popup.close();
      popupRef.current = null;
      // A failed reconnect may already have deactivated the action / dropped its creds server-side, so re-fetch and reflect the real state instead of leaving a stale "Connected" (a brand-new draft has none).
      if (remoteMcpAction) {
        try {
          const refreshedAction = await fetchActionStatus(remoteMcpAction.id);
          setParams({ remoteMcpAction: refreshedAction });
          // Discovery/DCR can fail because the edited endpoint no longer uses OAuth. Keep the
          // OAuth retry panel, but reveal the regular probe button so headers or a now-public
          // endpoint can be tested and can explicitly discard the stale grant on success.
          updateProviderLabel(refreshedAction.oauth?.provider_label ?? '');
          setAllowRegularProbe(failureReason === 'start_failed');
        } catch {
          /* ignore; fall through to the revert phase */
        }
      }
      setPhase(revertPhaseRef.current);
    } finally {
      openingRef.current = false;
    }
  }, [
    capture,
    captureOAuthFailure,
    createRemoteMcpMutation,
    updateRemoteMcpMutation,
    getConnectFormData,
    oauthStartMutation,
    oauthDisconnectMutation,
    remoteMcpAction,
    setParams,
    startWatcher,
    t,
    updateProviderLabel,
  ]);

  const reconnect = useCallback(() => {
    if (remoteMcpAction)
      updateProviderLabel(remoteMcpAction.oauth?.provider_label || providerLabelRef.current);
    setAllowRegularProbe(false);
    void openConsentPopup();
  }, [openConsentPopup, remoteMcpAction, updateProviderLabel]);

  const disconnect = useCallback(() => {
    if (!remoteMcpAction) return;
    oauthDisconnectMutation.mutate(
      { id: remoteMcpAction.id },
      {
        onSuccess: (action) => {
          setParams({ remoteMcpAction: action });
          capture(PostHogEventEnum.RemoteMcpDisconnected, {
            provider_label: providerLabelRef.current,
          });
          toast.success(t('oauth.disconnected', { provider: providerLabelRef.current }));
        },
      },
    );
  }, [capture, oauthDisconnectMutation, remoteMcpAction, setParams, t]);

  const cancel = useCallback(() => {
    isCancelRequestedRef.current = true;
    void settleByStatus(actionIdRef.current, 'idle');
  }, [settleByStatus]);

  return {
    phase,
    providerLabel,
    allowRegularProbe,
    beginConsent,
    openConsentPopup,
    reconnect,
    disconnect,
    cancel,
    isDisconnecting: oauthDisconnectMutation.isPending,
  };
};
